<template>
  <TransitionRoot
    :show="open"
    as="template"
    appear
    @after-leave="query = ''"
  >
    <Dialog
      class="relative z-100"
      @close="open = false"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to=""
        leave="ease-in duration-200"
        leave-from=""
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-zinc-500/25 transition-opacity dark:bg-zinc-900/50" />
      </TransitionChild>

      <div class="fixed inset-0 z-100 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to=" scale-100"
          leave="ease-in duration-200"
          leave-from=" scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="
              mx-auto
              max-w-2xl
              transform
              divide-y
              divide-zinc-500/10
              overflow-hidden
              rounded-xl
              bg-white/80
              shadow-2xl
              outline-1
              outline-black/5
              backdrop-blur-sm
              backdrop-filter
              transition-all
              dark:divide-white/10
              dark:bg-zinc-900/80
              dark:-outline-offset-1
              dark:outline-white/10
            "
          >
            <Combobox
              v-model="selectedResult"
              @update:model-value="onSelect"
            >
              <div class="grid grid-cols-1">
                <ComboboxInput
                  class="
                    col-start-1
                    row-start-1
                    h-12
                    w-full
                    bg-transparent
                    pr-4
                    pl-11
                    text-base
                    text-zinc-900
                    outline-hidden
                    placeholder:text-zinc-500
                    sm:text-sm
                    dark:text-white
                    dark:placeholder:text-zinc-400
                  "
                  placeholder="Search..."
                  autocomplete="off"
                  @input="query = $event.target.value"
                  @blur.capture.stop
                />
                <SearchOutlined
                  class="
                    pointer-events-none
                    col-start-1
                    row-start-1
                    ml-4
                    size-5
                    self-center
                    text-zinc-900/40!
                    dark:text-zinc-500!
                  "
                  aria-hidden="true"
                />
              </div>

              <ComboboxOptions
                v-if="query === '' || filteredInternalPages.length > 0 || searchResults.length > 0 || searchLoading"
                static
                as="ul"
                class="max-h-80 scroll-py-2 divide-y divide-zinc-500/10 overflow-y-auto dark:divide-white/5"
              >
                <li
                  v-if="query !== '' && (searchResults.length > 0 || searchLoading)"
                  class="p-2"
                >
                  <h2 class="my-2 px-3 text-xs font-semibold text-zinc-900 dark:text-white">
                    Search Results
                  </h2>
                  <ul class="text-sm text-zinc-700 dark:text-zinc-300">
                    <li
                      v-if="searchLoading"
                      class="px-3 py-2 text-zinc-500 dark:text-zinc-400"
                    >
                      Searching...
                    </li>
                    <ComboboxOption
                      v-for="result in searchResults"
                      :key="result.objectID"
                      v-slot="{ active }"
                      :value="result"
                      as="template"
                    >
                      <li
                        :class="[
                          'flex cursor-pointer items-center rounded-md px-3 py-2 select-none',
                          active && 'bg-primary/11 text-zinc-900 outline-hidden dark:text-white'
                        ]"
                      >
                        <span class="ml-3 flex-auto truncate">
                          <span class="font-medium">{{ result.name }}</span>
                          <span class="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
                            {{ result.type }}
                          </span>
                        </span>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
                <li
                  v-if="query === '' || filteredInternalPages.length > 0"
                  class="p-2"
                >
                  <h2
                    class="my-2 px-3 text-xs font-semibold text-zinc-900 dark:text-white"
                  >
                    Pages & Actions
                  </h2>
                  <ul class="text-sm text-zinc-700 dark:text-zinc-300">
                    <ComboboxOption
                      v-for="page in filteredInternalPages"
                      :key="page.id"
                      v-slot="{ active }"
                      :value="page"
                      as="template"
                    >
                      <li
                        :class="[
                          'flex cursor-pointer items-center rounded-md px-3 py-2 select-none',
                          active && 'bg-primary/11 text-zinc-900 outline-hidden dark:text-white'
                        ]"
                      >
                        <span class="ml-3 flex-auto truncate">{{ page.name }}</span>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
              </ComboboxOptions>

              <div
                v-if="query !== '' && !searchLoading && searchResults.length === 0
                  && filteredInternalPages.length === 0"
                class="px-6 py-14 text-center sm:px-14"
              >
                <p class="mt-4 text-sm text-zinc-900 dark:text-zinc-300">
                  We couldn't find any results with that term. Please try again.
                </p>
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { SearchOutlined } from '@ant-design/icons-vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { message } from 'ant-design-vue';
import { useStore } from '../../api/index.ts';
import { useRouter } from 'vue-router';
import { setHTMLDarkMode } from '../../assets/js/helpers/DarkThemeHelper.ts';
import { liteClient as algoliasearch, type LiteClient } from 'algoliasearch/lite';
import type { Hit, SearchResponse } from 'algoliasearch';
import { getClient } from '@sentry/browser';

const store = useStore();
const router = useRouter();

function teamToOption(team: { id: string; name: string; color: { red: number; green: number; blue: number } | null }) {
  const color = team.color
    ? '#' + (1 << 24 | team.color.red << 16 | team.color.green << 8 | team.color.blue).toString(16).slice(1)
    : null;
  return { value: team.id, label: team.name, color };
}

const userMode = computed(() => store.userMode);
const userTeams = computed(() => store.userTeams);
const darkMode = computed(() => store.userPreferences.darkMode);

const searchClient = ref<null | LiteClient>(null);
const searchKey = ref(store.userMode == 'user' ? store.searchKey : store.teamData?.searchKey);
const searchResults = ref<Hit[]>([]);
const searchLoading = ref(false);

// Can be a quickAction, internal page, or a Hit
type CommandPaletteItem =
  | {
    url?: string & Location;
    id: string;
    action?: () => void;
    path?: string;
    query?: { tab?: string };
    type?: string;
  }
  | Hit;

const selectedResult = ref<CommandPaletteItem | null>(null);

function isSearchHit(item: CommandPaletteItem | null): item is Hit {
  return !!item && typeof item === 'object' && 'objectID' in item;
}

const internalPages = computed(() => {
  const pages = [
    { name: 'Add new rack...', action: addRack, id: 'add-rack' },
    { name: 'Add new room...', action: addRoom, id: 'add-room' },
    { name: 'Dashboard', id: 'dashboard', path: '/app/dashboard' },
    { name: 'Feedback', id: 'feedback', url: 'https://rackmanage.featurebase.app/?createPost=true' },
    { name: 'Help', id: 'help', url: 'https://support.rackmanage.io/' },
    { name: 'Logout', id: 'logout', action: logout },
    { name: 'Racks', id: 'racks', path: '/app/racks' },
    { name: 'Report bug', id: 'report-bug', action: launchFeedback },
    { name: 'Rooms', id: 'rooms', path: '/app/rooms' },
    { name: 'Settings', id: 'settings', path: '/app/settings' },
    { name: 'Settings → Account', id: 'settings-account', path: '/app/settings', query: { tab: '1' } },
    { name: 'Settings → API', id: 'settings-api', path: '/app/settings', query: { tab: '2' } },
    { name: 'Settings → Domains & Branding', id: 'settings-branding', path: '/app/settings', query: { tab: '3' } },
    { name: 'Settings → Teams', id: 'settings-teams', path: '/app/settings', query: { tab: '4' } },
    { name: 'Slot Types', id: 'slots', path: '/app/types' },
  ];

  // Billing - only show if userMode == 'user'
  if (userMode.value === 'user') {
    pages.splice(3, 0, { name: 'Billing', id: 'billing', path: '/app/settings/billing' });
  }

  // Team mode / User mode - only show if team != null
  if (userTeams.value.length > 0) {
    if (userMode.value === 'user') {
      userTeams.value.forEach((team) => {
        pages.push({
          name: 'Switch to ' + team.name,
          id: team.id,
          action: () => store.switchWorkspaceWithPopup(teamToOption(team)),
        });
      });
    } else {
      pages.push({
        name: 'Switch to Personal Workspace',
        id: 'personal-workspace',
        action: () => store.switchWorkspaceWithPopup({ value: 'user', label: 'Personal Workspace', color: null }),
      });
      userTeams.value.filter((team) => team.id !== store.team).forEach((team) => {
        pages.push({
          name: 'Switch to ' + team.name,
          id: team.id,
          action: () => store.switchWorkspaceWithPopup(teamToOption(team)),
        });
      });
    }
  }

  // Light/Dark mode - always show (opposite of current mode)
  if (darkMode.value) {
    pages.push({ name: 'Light mode', id: 'light-mode', action: () => setDarkMode(false) });
  } else {
    pages.push({ name: 'Dark mode', id: 'dark-mode', action: () => setDarkMode(true) });
  }

  return pages.sort((a, b) => a.name.localeCompare(b.name));
});

const query = ref('');
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const filteredInternalPages = computed(() =>
  query.value === ''
    ? internalPages.value
    : internalPages.value.filter((page) => {
      return page.name.toLowerCase().includes(query.value.toLowerCase());
    }),
);

// Watch for search key changes to initialize search client
watch(searchKey, (val) => {
  if (val != null) {
    searchClient.value = algoliasearch(
      import.meta.env.VITE_ALGOLIA_APP_ID,
      val,
    );
  }
}, { immediate: true, deep: true });

// Watch query to perform search
watch(query, (newQuery) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!newQuery || newQuery.trim() === '') {
    searchResults.value = [];
    return;
  }

  // Debounce search
  let currentSearchToken = {};
  searchTimeout = setTimeout(async () => {
    const searchToken = {};
    currentSearchToken = searchToken;
    const results = await performSearch(newQuery);
    if (currentSearchToken === searchToken && results) {
      searchResults.value = results;
    }
  }, 300);
});

async function performSearch(searchQuery: string): Promise<Hit[] | null> {
  if (!searchClient.value || !searchQuery || searchQuery.trim() === '') {
    return [];
  }

  searchLoading.value = true;
  try {
    const response = await searchClient.value.search<Hit>([
      {
        indexName: 'rm_users',
        params: {
          query: searchQuery,
          hitsPerPage: 5, // Limit results for command palette
        },
      },
    ]);

    const result = response?.results?.[0];
    if (result && 'hits' in result) {
      return (result as SearchResponse<Hit>).hits;
    }
    return [];
  }
  catch (err: any) {
    console.error('Search error:', err);
    return [];
  }
  finally {
    searchLoading.value = false;
  }
}

onMounted(() => {
  getSearchKey();
});

async function getSearchKey() {
  if (searchKey.value == null) {
    if (store.userMode !== 'team') {
      searchKey.value = await store.getSearchKey(store.user?.uid);
    } else {
      searchKey.value = await store.getTeamSearchKey();
    }
  }
}

function onSelect(item: CommandPaletteItem | null) {
  if (!item) return;

  if (isSearchHit(item)) {
    const resultType = (item as any).type;
    const type = (typeof resultType === 'string' ? resultType.toLowerCase() : 'rack') || 'rack';
    const objectID = item.objectID;
    const targetPath = `/app/${type}s/${objectID}/view`;

    // Check if we're already on this route - if so, force navigation by replacing first
    const currentPath = router.currentRoute.value.path;
    if (currentPath === targetPath) {
      // Force navigation by replacing with a query parameter, then navigating to the target
      const currentQuery = { ...router.currentRoute.value.query };
      router.replace({ path: targetPath, query: { ...currentQuery, _refresh: Date.now().toString() } }).then(() => {
        router.replace({ path: targetPath, query: currentQuery });
      });
    } else {
      router.push(targetPath);
    }
    open.value = false;
    return;
  }

  if (item.action && typeof item.action === 'function') {
    item.action();
    open.value = false; // or handle inside action
  } else if (item.path) {
    router.push({ path: item.path, query: item.query });
    open.value = false;
  } else if (item.url) {
    window.location = item.url;
  }
}

const open = defineModel('open', { type: Boolean, default: false });

async function addRack() {
  try {
    const rackId = await store.addRack();
    router.push('/app/racks/' + rackId + '/edit');
  }
  catch (err: any) {
    message.error(err.message);
  }
}

async function addRoom() {
  try {
    const roomId = await store.addRoom();
    router.push('/app/rooms/' + roomId + '/edit');
  }
  catch (err: any) {
    message.error(err.message);
  }
}

async function logout() {
  try {
    if (await store.isSSOUser()) {
      await store.initiateSSOLogout();
      return;
    }
    await store.logout();
    window.location.href = '/auth/login';
  }
  catch (err: any) {
    message.error(err.message || 'Failed to logout');
  }
}

async function setDarkMode(darkMode: boolean) {
  store.changeDarkMode(darkMode).then(() => {
    setHTMLDarkMode(darkMode);
    message.success('Dark mode updated.');
    store.userPreferences.darkMode = darkMode;
  }).catch((err: any) => {
    message.error(err.message || 'Failed to update dark mode');
  });
}

const launchFeedback = async () => {
  const client = getClient();
  const feedback = client?.getIntegrationByName('Feedback');
  if (feedback) {
    // @ts-expect-error - Feedback integration has attachTo method
    const form = await feedback?.createForm();
    form.appendToDom();
    form.open();
  }
};
</script>
